import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BlogShell from "./BlogShell";
import BlogCard from "./BlogCard";
import LeadMagnet from "./LeadMagnet";
import TableOfContents from "./TableOfContents";
import {
  buildBlogUrl,
  fetchBlogArticle,
  fetchRelatedBlogArticles,
  resolveBlogImageUrl,
} from "./blogApi";
import {
  buildHeadingId,
  formatArticleDocumentTitle,
  formatBlogDate,
  getArticleToc,
  sanitizeRichHtml,
} from "./blogUtils";
import useSeoMetadata from "./useSeoMetadata";
import { trackArticleRead, trackEvent } from "../../utils/analytics";
import { copyText } from "../../utils/clipboard";

// Абзацы и пункты списков приходят markdown-ом: ссылки на источники должны
// быть кликабельными, иначе читатель видит [текст](url) как есть
const INLINE_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

function renderInline(text, keyPrefix = "") {
  const source = String(text || "");
  const parts = [];
  let lastIndex = 0;
  let match;

  INLINE_LINK_RE.lastIndex = 0;
  while ((match = INLINE_LINK_RE.exec(source)) !== null) {
    if (match.index > lastIndex) {
      parts.push(source.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={`${keyPrefix}-link-${match.index}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (!parts.length) return source;
  if (lastIndex < source.length) parts.push(source.slice(lastIndex));
  return parts;
}

function renderBlock(block) {
  if (block.type === "heading") {
    const Tag = block.level === 3 ? "h3" : "h2";
    return (
      <div className="blog-article__block" key={`${block.type}-${block.text}`}>
        <Tag id={buildHeadingId(block.text)}>{block.text}</Tag>
      </div>
    );
  }

  if (block.type === "list") {
    const ListTag = block.ordered ? "ol" : "ul";
    return (
      <div
        className="blog-article__block"
        key={`${block.type}-${block.items.join("-")}`}
      >
        <ListTag>
          {block.items.map((item) => (
            <li key={item}>{renderInline(item, item)}</li>
          ))}
        </ListTag>
      </div>
    );
  }

  if (block.type === "code") {
    return (
      <div className="blog-article__block" key={`${block.type}-${block.code}`}>
        <pre className="blog-article__code">
          <code>{block.code}</code>
        </pre>
      </div>
    );
  }

  return (
    <div className="blog-article__block" key={`${block.type}-${block.text}`}>
      <p>{renderInline(block.text, block.text)}</p>
    </div>
  );
}

function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeHeading, setActiveHeading] = useState("");
  const [stickyVisible, setStickyVisible] = useState(false);
  const [stickyClosed, setStickyClosed] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const tocItems = useMemo(() => getArticleToc(article), [article]);
  const articleUrl = buildBlogUrl(`/blog/${slug || ""}`);
  const articleImage = resolveBlogImageUrl(article?.ogImage);
  const htmlContent = useMemo(
    () =>
      article?.contentFormat === "html"
        ? sanitizeRichHtml(article.content || "")
        : "",
    [article]
  );

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setStickyClosed(false);

    fetchBlogArticle(slug)
      .then(async (nextArticle) => {
        if (cancelled) return;
        setArticle(nextArticle);

        if (!nextArticle) {
          setRelatedArticles([]);
          setLoading(false);
          return;
        }

        const nextRelated = await fetchRelatedBlogArticles(nextArticle, 3);
        if (cancelled) return;
        setRelatedArticles(nextRelated);
        setLoading(false);
      })
      .catch((nextError) => {
        if (cancelled) return;
        setError(nextError);
        setArticle(null);
        setRelatedArticles([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    if (!article) {
      return;
    }
    trackArticleRead(article);
  }, [article]);

  useEffect(() => {
    setActiveHeading(tocItems[0]?.id || "");
  }, [tocItems]);

  useEffect(() => {
    if (!shareCopied) return undefined;

    const timerId = window.setTimeout(() => {
      setShareCopied(false);
    }, 2000);

    return () => window.clearTimeout(timerId);
  }, [shareCopied]);

  useEffect(() => {
    if (!tocItems.length) return undefined;

    const onScroll = () => {
      const passed = window.scrollY > 520;
      setStickyVisible(passed && !stickyClosed);

      let current = tocItems[0]?.id || "";
      tocItems.forEach((item) => {
        const element = document.getElementById(item.id);
        if (element && element.getBoundingClientRect().top <= 140) {
          current = item.id;
        }
      });
      setActiveHeading(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [tocItems, stickyClosed]);

  useSeoMetadata({
    title: article
      ? formatArticleDocumentTitle(article.title)
      : loading
        ? "Загрузка статьи | Блог Энилекс.Контроль"
        : "Статья не найдена | Блог Энилекс.Контроль",
    description:
      article?.description ||
      "Статья блога Энилекс.Контроль про маркетплейсы, аналитику, рекламу и рост продаж seller-команд.",
    canonicalUrl: article ? articleUrl : buildBlogUrl("/blog"),
    robots: article || loading ? "index,follow" : "noindex,nofollow",
    meta: article
      ? [
          {
            name: "author",
            content: article.author,
          },
        ]
      : [],
    openGraph: {
      type: article ? "article" : "website",
      title: article?.title || "Блог Энилекс.Контроль",
      description:
        article?.description ||
        "Статья блога Энилекс.Контроль про маркетплейсы, аналитику, рекламу и рост продаж seller-команд.",
      image: articleImage,
      url: article ? articleUrl : buildBlogUrl("/blog"),
      siteName: "Энилекс.Контроль",
      locale: "ru_RU",
      article: article
        ? {
            publishedTime: article.publishedAt,
            modifiedTime: article.updatedAt,
            author: article.author,
            section: article.category,
          }
        : undefined,
    },
    twitter: {
      card: "summary_large_image",
      title: article?.title || "Блог Энилекс.Контроль",
      description:
        article?.description ||
        "Статья блога Энилекс.Контроль про маркетплейсы, аналитику, рекламу и рост продаж seller-команд.",
      image: articleImage,
    },
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.description,
            image: articleImage,
            datePublished: article.publishedAt,
            dateModified: article.updatedAt,
            author: {
              "@type": "Organization",
              name: article.author,
              url: buildBlogUrl("/"),
            },
            publisher: {
              "@type": "Organization",
              name: "Энилекс.Контроль",
              logo: {
                "@type": "ImageObject",
                url: resolveBlogImageUrl("/brand/anylex-logo-cyr.png"),
              },
            },
            mainEntityOfPage: articleUrl,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Блог",
                item: buildBlogUrl("/blog"),
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.category,
                item: buildBlogUrl(`/blog/category/${article.categorySlug}`),
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: articleUrl,
              },
            ],
          },
        ]
      : [],
  });

  if (loading) {
    return (
      <BlogShell>
        <section className="blog-panel blog-status" aria-busy="true">
          <h1>Загружаем статью</h1>
          <p>Подтягиваем контент, структуру и SEO-метаданные.</p>
        </section>
      </BlogShell>
    );
  }

  if (error) {
    return (
      <BlogShell>
        <section className="blog-panel blog-status blog-status--error">
          <h1>Не удалось загрузить статью</h1>
          <p>Попробуйте обновить страницу или вернитесь к списку материалов.</p>
          <Link className="btn" to="/blog">
            Вернуться к блогу
          </Link>
        </section>
      </BlogShell>
    );
  }

  if (!article) {
    return (
      <BlogShell>
        <section className="blog-panel blog-empty">
          <h1>Статья не найдена</h1>
          <p>Похоже, такого slug в блоге пока нет.</p>
          <Link className="btn" to="/blog">
            Вернуться к списку статей
          </Link>
        </section>
      </BlogShell>
    );
  }

  return (
    <BlogShell>
      <nav
        className="breadcrumbs"
        aria-label="Breadcrumbs"
        data-testid="breadcrumbs"
      >
        <Link to="/blog">Главная блога</Link>
        <span className="breadcrumbs__separator">→</span>
        <Link to="/blog">Блог</Link>
        <span className="breadcrumbs__separator">→</span>
        <Link to={`/blog/category/${article.categorySlug}`}>{article.category}</Link>
        <span className="breadcrumbs__separator">→</span>
        <span className="breadcrumbs__current">{article.title}</span>
      </nav>

      <article className="blog-panel blog-article" data-testid="blog-article">
        <div className="blog-article__layout">
          <div className="blog-article__content">
            <header>
              <div className="blog-article__type">{article.contentType}</div>
              <h1 className="blog-article__title" data-testid="article-title">
                {article.title}
              </h1>
              <p className="blog-article__lede">{article.hero}</p>
              <div className="blog-article__meta">
                <span>{formatBlogDate(article.publishedAt)}</span>
                <span>{article.category}</span>
                <span>{article.readingTime} мин чтения</span>
                <span>{article.author}</span>
              </div>
              <div className="blog-article__socials">
                <button
                  type="button"
                  className="blog-article__action"
                  onClick={async () => {
                    const copied = await copyText(articleUrl);
                    setShareCopied(copied);
                    trackEvent("article_share_link_copy", {
                      article_slug: article.slug,
                      article_category: article.categorySlug,
                      copied_successfully: copied,
                    });
                  }}
                >
                  {shareCopied ? "Ссылка скопирована" : "Скопировать ссылку"}
                </button>
                <Link
                  className="blog-article__action blog-article__action--accent"
                  to={`/blog/category/${article.categorySlug}`}
                >
                  Еще по теме
                </Link>
              </div>
            </header>

            <figure className="blog-article__hero-media">
              <img
                className="blog-article__hero-image"
                src={articleImage}
                alt={article.title}
              />
              <figcaption className="blog-article__hero-caption">
                Инфографика и обложка материала
              </figcaption>
            </figure>

            <div className="blog-article__body" data-testid="article-content">
              {article.contentFormat === "html" ? (
                <>
                  <div
                    className="blog-article__html"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                  />
                  <LeadMagnet variant={article.leadMagnet} cta={article.cta} />
                </>
              ) : (
                article.blocks.map((block, index) => (
                  <React.Fragment key={`${block.type}-${index}`}>
                    {index === 3 ? <LeadMagnet variant={article.leadMagnet} cta={article.cta} /> : null}
                    {renderBlock(block)}
                  </React.Fragment>
                ))
              )}
            </div>

            {article.telegramUrl ? (
              <section className="blog-article__discuss">
                <p className="blog-article__discuss-text">
                  Обсуждение этой статьи идёт в нашем телеграм-канале.
                </p>
                <a
                  className="blog-article__action blog-article__action--accent"
                  href={article.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent("article_telegram_click", { article_slug: article.slug })
                  }
                >
                  Оставить комментарий в Телеграме
                </a>
              </section>
            ) : null}

            {relatedArticles.length ? (
              <section className="blog-related">
                <h2>Похожие статьи</h2>
                <div className="blog-grid">
                  {relatedArticles.map((item) => (
                    <BlogCard article={item} key={item.slug} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="blog-article__sidebar">
            <TableOfContents items={tocItems} activeId={activeHeading} />
            <div className="blog-article__sidebar-card">
              <p className="blog-article__sidebar-title">Категория</p>
              <p className="blog-article__sidebar-copy">
                {article.categoryDescription}
              </p>
              <Link
                className="blog-article__action blog-article__action--accent"
                to={`/blog/category/${article.categorySlug}`}
              >
                Все статьи категории
              </Link>
            </div>
          </aside>
        </div>
      </article>

      {stickyVisible ? (
        <LeadMagnet
          sticky
          cta={article.cta}
          variant={article.leadMagnet}
          onClose={() => setStickyClosed(true)}
        />
      ) : null}
    </BlogShell>
  );
}

export default BlogArticle;
